import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { SlotPicker } from './ScheduleActionModals';

// Admin read-only detail popup for a new-client request: contact links the
// office can act on immediately (tel:/mailto:), the appointment + therapist,
// the decision + note, and the full Acuity intake answers.
const STATUS_LABEL = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  info_admin: 'Office follow-up',
  info_self: 'Therapist reaching out',
};
const NOTE_LABEL = {
  rejected: 'Reason for rejection',
  info_admin: 'Questions for the office',
};
// Distinct from --accent-color (used by the adjacent decision chips), --success-color,
// --primary-color, and --muted-color — see AdminNewClientRequestsPage's OFFICE_CHIP.confirmed.
const CONFIRMED_COLOR = '#0E7490';
// Intake keys already surfaced in the contact block — skip in the list below.
const CONTACT_KEYS = new Set(['firstName', 'lastName', 'email', 'phone']);

export default function NewClientDetailModal({ acuityId, onClose, officeActions = false, therapists = [], onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [actionView, setActionView] = useState(null); // 'confirm' | 'complete' | 'reassign' | 'refer' | 'move'
  const [actionNote, setActionNote] = useState('');
  const [reassignTo, setReassignTo] = useState('');
  const [moveTypeId, setMoveTypeId] = useState('');
  const [moveTypes, setMoveTypes] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionWarnings, setActionWarnings] = useState([]);

  useEffect(() => {
    setActionView(null); setActionNote(''); setReassignTo('');
    setMoveTypeId(''); setMoveTypes(null); setActionError(''); setActionWarnings([]);
    let active = true;
    API.get(`/admin/new-client-requests/${acuityId}/detail`)
      .then((res) => { if (active) setDetail(res.data || null); })
      .catch(() => { if (active) setError("Couldn't load this request — try again in a moment."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [acuityId]);

  const intakeEntries = detail && detail.intake
    ? Object.entries(detail.intake).filter(([k, v]) => v && !CONTACT_KEYS.has(k))
    : [];

  const targetTherapist = therapists.find((t) => String(t.id) === String(reassignTo || detail?.reassignedTo));

  const postStatus = async (status, extra = {}) => {
    setSubmitting(true); setActionError('');
    try {
      const res = await API.post(`/admin/new-client-requests/${acuityId}/office-status`, { status, ...extra });
      const warnings = res.data?.warnings || [];
      setActionWarnings(warnings);
      if (onChanged) onChanged();
      if (status === 'reassigned') {
        setReassignTo(String(extra.reassignedTo));
        setActionView('move');
        loadMoveTypes(therapists.find((t) => String(t.id) === String(extra.reassignedTo))?.calendarId);
      } else {
        setActionView(null);
        // Keep the modal open when a side effect failed — the admin must see
        // the "cancel it manually" warning before it disappears.
        if (warnings.length === 0) onClose();
      }
    } catch (err) {
      setActionError(err.response?.data?.message || 'Could not save — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const loadMoveTypes = (calendarId) => {
    if (!calendarId) return;
    setMoveTypes(null);
    API.get('/acuity/appointment-types', { params: { calendarId } })
      .then((res) => setMoveTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMoveTypes([]));
  };

  const doMove = async (picked) => {
    setSubmitting(true); setActionError('');
    try {
      const res = await API.post(`/admin/new-client-requests/${acuityId}/move`, {
        appointmentTypeId: Number(moveTypeId), datetime: picked.time,
      });
      const warnings = res.data?.warnings || [];
      setActionWarnings(warnings);
      if (onChanged) onChanged();
      setActionView(null);
      if (warnings.length === 0) onClose();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Could not move — the slot may have just been taken.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0 }}>
            {detail ? detail.clientName || 'New client request' : 'New client request'}
          </h3>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">&times;</button>
        </div>

        {loading && <div style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Loading…</div>}
        {error && <div style={errorStyle}>{error}</div>}

        {detail && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={chipStyle}>{STATUS_LABEL[detail.status] || 'Pending'}</span>
              {detail.canceled && detail.status !== 'rejected' && (
                <span style={{ ...chipStyle, background: 'var(--muted-color)' }}>Appointment canceled</span>
              )}
              {detail.decidedAt && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  decided {new Date(detail.decidedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>

            {/* Contact block — the reason this popup exists */}
            <div style={contactBoxStyle}>
              {detail.clientPhone ? (
                <a href={`tel:${detail.clientPhone}`} style={contactLinkStyle}>📞 {detail.clientPhone}</a>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>📞 No phone on file</span>
              )}
              {detail.clientEmail ? (
                <a href={`mailto:${detail.clientEmail}`} style={contactLinkStyle}>✉️ {detail.clientEmail}</a>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>✉️ No email on file</span>
              )}
            </div>

            <div style={{ fontSize: 14, marginBottom: 12 }}>
              {detail.apptDatetime && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Appointment:</strong>{' '}
                  {new Date(detail.apptDatetime).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}
                </div>
              )}
              <div><strong>Therapist:</strong> {detail.therapistName}</div>
            </div>

            {detail.note && (
              <div style={noteBoxStyle}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  {NOTE_LABEL[detail.status] || 'Note from therapist'}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{detail.note}</div>
              </div>
            )}

            <div style={{ fontWeight: 700, fontSize: 13, margin: '14px 0 6px', color: 'var(--text-strong)' }}>
              Acuity intake details
            </div>
            {detail.intake === null ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Couldn't reach Acuity for the intake answers right now — contact info above is from the last sync.
              </div>
            ) : intakeEntries.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No additional intake details.</div>
            ) : (
              <div style={intakeBoxStyle}>
                {intakeEntries.map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{labelize(k)}: </span>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {officeActions && (
              <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 14, paddingTop: 12 }}>
                {detail.officeStatus && (
                  <div style={{ fontSize: 13, marginBottom: 10, color: 'var(--text-strong)' }}>
                    Office status: <strong>{detail.officeStatus === 'completed' ? 'Intake completed' : detail.officeStatus === 'confirmed' ? 'Confirmed for therapist' : detail.officeStatus === 'referred_out' ? 'Referred out' : `Reassigned to ${detail.reassignedToName || '—'}`}</strong>
                    {detail.officeStatus === 'reassigned' && !detail.appointmentMoved && (
                      <span style={{ marginLeft: 8, background: '#fff3cd', color: '#7a5b00', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                        appointment not moved yet
                      </span>
                    )}
                    {detail.officeNote && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{detail.officeNote}</div>}
                  </div>
                )}
                {actionError && <div style={errorStyle}>{actionError}</div>}
                {actionWarnings.map((w, i) => (
                  <div key={i} style={{ background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '6px 10px', marginBottom: 6, fontSize: 12 }}>⚠ {w}</div>
                ))}

                {actionView === null && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {detail.officeStatus !== 'reassigned' && (
                      <button type="button" style={actBtn(CONFIRMED_COLOR)} disabled={submitting} onClick={() => { setActionNote(''); setActionView('confirm'); }}>✅ Confirm for therapist</button>
                    )}
                    <button type="button" style={actBtn('var(--success-color)')} disabled={submitting} onClick={() => { setActionNote(''); setActionView('complete'); }}>✓ Mark intake completed</button>
                    <button type="button" style={actBtn('var(--primary-color)')} disabled={submitting} onClick={() => { setActionNote(''); setReassignTo(''); setActionView('reassign'); }}>↪ Reassign</button>
                    <button type="button" style={actBtn('var(--error-color)')} disabled={submitting} onClick={() => { setActionNote(''); setActionView('refer'); }}>⤴ Refer out</button>
                    {detail.officeStatus === 'reassigned' && !detail.appointmentMoved && (
                      <button type="button" style={actBtn('var(--accent-color)')} disabled={submitting} onClick={() => { setActionView('move'); loadMoveTypes(targetTherapist?.calendarId); }}>📅 Move appointment now</button>
                    )}
                  </div>
                )}

                {actionView === 'complete' && (
                  <div>
                    <textarea style={taStyle} placeholder="Optional note" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" style={actBtn('var(--success-color)')} disabled={submitting} onClick={() => postStatus('completed', { note: actionNote })}>{submitting ? 'Saving…' : 'Confirm completed'}</button>
                      <button type="button" style={ghostBtn} disabled={submitting} onClick={() => setActionView(null)}>Back</button>
                    </div>
                  </div>
                )}

                {actionView === 'confirm' && (
                  <div>
                    <div style={{ fontSize: 13, marginBottom: 6 }}>
                      Marks this client confirmed and ready — the therapist sees a dashboard banner prompting them to create the profile and send the welcome letter.
                    </div>
                    <textarea style={taStyle} placeholder="Optional note for the therapist" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" style={actBtn(CONFIRMED_COLOR)} disabled={submitting} onClick={() => postStatus('confirmed', { note: actionNote })}>{submitting ? 'Saving…' : 'Confirm client'}</button>
                      <button type="button" style={ghostBtn} disabled={submitting} onClick={() => setActionView(null)}>Back</button>
                    </div>
                  </div>
                )}

                {actionView === 'reassign' && (
                  <div>
                    <select style={selStyle} value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                      <option value="">Reassign to…</option>
                      {therapists.map((t) => (
                        <option key={t.id} value={t.id} disabled={!t.calendarId}>
                          {t.name}{t.calendarId ? '' : ' (no calendar)'}
                        </option>
                      ))}
                    </select>
                    <textarea style={taStyle} placeholder="Optional note (why)" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" style={actBtn('var(--primary-color)')} disabled={submitting || !reassignTo} onClick={() => postStatus('reassigned', { reassignedTo: Number(reassignTo), note: actionNote })}>{submitting ? 'Saving…' : 'Record reassignment'}</button>
                      <button type="button" style={ghostBtn} disabled={submitting} onClick={() => setActionView(null)}>Back</button>
                    </div>
                  </div>
                )}

                {actionView === 'refer' && (
                  <div>
                    <div style={{ fontSize: 13, marginBottom: 6 }}>
                      Recording this cancels the appointment in Acuity — the client receives the standard cancellation notice. Your note stays internal.
                    </div>
                    <textarea style={taStyle} placeholder="Where / why (required)" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" style={actBtn('var(--error-color)')} disabled={submitting || !actionNote.trim()} onClick={() => postStatus('referred_out', { note: actionNote })}>{submitting ? 'Saving…' : 'Confirm refer out'}</button>
                      <button type="button" style={ghostBtn} disabled={submitting} onClick={() => setActionView(null)}>Back</button>
                    </div>
                  </div>
                )}

                {actionView === 'move' && (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                      Move the booking to {targetTherapist?.name || 'the new therapist'}
                    </div>
                    {!targetTherapist?.calendarId ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>That therapist has no active calendar — book manually via the Calendar Manager.</div>
                    ) : (
                      <>
                        <select style={selStyle} value={moveTypeId} onChange={(e) => setMoveTypeId(e.target.value)}>
                          <option value="">Choose appointment type…</option>
                          {(moveTypes || []).map((t) => (
                            <option key={t.id} value={t.id}>{t.name} ({t.duration} min)</option>
                          ))}
                        </select>
                        {moveTypeId && (
                          <SlotPicker calendarId={targetTherapist.calendarId} typeId={moveTypeId} onPick={doMove} />
                        )}
                      </>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <button type="button" style={ghostBtn} disabled={submitting} onClick={() => { setActionView(null); onClose(); }}>Move later</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function labelize(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const boxStyle = { background: 'var(--white)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 540, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 };
const closeBtnStyle = { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)' };
const chipStyle = { background: 'var(--primary-color)', color: '#fff', borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 600 };
const contactBoxStyle = { display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12, marginBottom: 12 };
const contactLinkStyle = { color: 'var(--primary-color)', fontWeight: 700, fontSize: 16, textDecoration: 'none' };
const noteBoxStyle = { background: 'var(--secondary-color)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12 };
const intakeBoxStyle = { background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12, fontSize: 14, maxHeight: 240, overflowY: 'auto' };
const errorStyle = { background: '#f8d7da', color: '#721c24', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 14 };
const actBtn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' });
const ghostBtn = { background: 'transparent', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const taStyle = { width: '100%', minHeight: 60, borderRadius: 8, border: '1px solid var(--border-color)', padding: 8, fontSize: 13, margin: '6px 0', fontFamily: 'inherit' };
const selStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, marginBottom: 4 };
